import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  View, Text, FlatList, TouchableOpacity, StyleSheet, ActivityIndicator,
  TextInput,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../../hooks/useAuth';
import { NavBackButton } from '../../components/NavBackButton';
import { createActionUsageRecords, getUsageSessionsPage, getUsageSessionsByStore, getAllStoreInventory, getUsageSessionsByActionNumber, getStores } from '../../services/storeService';
import type { UsageSessionsPage } from '../../services/storeService';
import { getEquipment, getCategories } from '../../services/inventoryService';
import { getPublicUsers } from '../../services/userService';
import { UsageSession, Store, Equipment, Category, StoreInventory } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import NewUsageWizard from '../../components/NewUsageWizard';

const PAGE_SIZE = 25;

export default function UsageScreen() {
  const { appUser, firebaseUser } = useAuth();
  const { t } = useLanguage();
  const router = useRouter();
  const navigation = useNavigation();
  const params = useLocalSearchParams<{ storeId?: string; taskId?: string; create?: string; type?: string; equipmentId?: string }>();
  const storeId = params.storeId ?? null;

  const [sessions, setSessions] = useState<UsageSession[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [storeInventory, setStoreInventory] = useState<StoreInventory[]>([]);
  const [publicUsers, setPublicUsers] = useState<Record<string, { firstname: string; lastname: string }>>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sessionsCursorRef = useRef<UsageSessionsPage['cursor']>(null);
  const hasMoreSessionsRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const [wizardVisible, setWizardVisible] = useState(false);
  const launched = useRef('');
  useEffect(() => {
    const key = [params.taskId, params.create, params.storeId, params.equipmentId].join(':');
    if (!loading && canCreate && (params.taskId || params.create) && launched.current !== key) {
      launched.current = key; setWizardVisible(true);
    }
  }, [loading, params.taskId, params.create, params.storeId, params.equipmentId, appUser?.role]); // eslint-disable-line react-hooks/exhaustive-deps
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [searchMode, setSearchMode] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UsageSession[]>([]);
  const [searching, setSearching] = useState(false);

  const isAdminOrManager = appUser?.role === 'admin' || appUser?.role === 'manager';
  const canView = appUser?.role !== 'guest';
  const canCreate =
    appUser?.role === 'user' ||
    appUser?.role === 'manager' ||
    appUser?.role === 'admin';

  // When viewing store history, admin/manager see all records; otherwise users see only their own
  const isStoreHistoryMode = isAdminOrManager && !!storeId;

  useLayoutEffect(() => {
    if (storeId) {
      navigation.setOptions({
        headerLeft: () => <NavBackButton onPress={() => router.navigate('/(app)/stores')} />,
      });
    }
  }, [storeId, navigation, router]);

  const load = useCallback(async () => {
    loadingMoreRef.current = false;
    setLoading(true);

    if (isStoreHistoryMode) {
      const [s, eq, cats, sess, inv, pu] = await Promise.all([
        getStores(),
        getEquipment(),
        getCategories(),
        getUsageSessionsByStore(storeId!),
        getAllStoreInventory(),
        getPublicUsers(),
      ]);
      setStores(s);
      setEquipment(eq);
      setCategories(cats);
      setSessions(sess);
      setStoreInventory(inv);
      setPublicUsers(pu);
    } else {
      const uid = firebaseUser?.uid ?? '';
      const [s, eq, cats, page, inv, pu] = await Promise.all([
        getStores(),
        getEquipment(),
        getCategories(),
        getUsageSessionsPage(uid, PAGE_SIZE, null),
        getAllStoreInventory(),
        getPublicUsers(),
      ]);
      setStores(s);
      setEquipment(eq);
      setCategories(cats);
      setSessions(page.sessions);
      sessionsCursorRef.current = page.cursor;
      hasMoreSessionsRef.current = page.hasMore;
      setStoreInventory(inv);
      setPublicUsers(pu);
    }
    setLoading(false);
    loadingMoreRef.current = false;
    setLoadingMore(false);
  }, [isStoreHistoryMode, storeId, firebaseUser?.uid]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (loading || loadingMoreRef.current || !hasMoreSessionsRef.current || isStoreHistoryMode || searchMode) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const page = await getUsageSessionsPage(firebaseUser?.uid ?? '', PAGE_SIZE, sessionsCursorRef.current);
      setSessions(prev => [...prev, ...page.sessions]);
      sessionsCursorRef.current = page.cursor;
      hasMoreSessionsRef.current = page.hasMore;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const handleSearch = async () => {
    const trimmed = searchInput.trim();
    if (!trimmed) return;
    setSearching(true);
    setSearchQuery(trimmed);
    const [results, pu, s] = await Promise.all([
      getUsageSessionsByActionNumber(trimmed),
      getPublicUsers(),
      getStores(),
    ]);
    setSearchResults(results);
    setPublicUsers(pu);
    setStores(s);
    setSearching(false);
  };

  const handleWizardSave = async (
    items: { equipmentId: string; quantity: number; itemType?: Equipment['itemType'] }[],
    meta: {
      type: 'take' | 'add' | 'transfer' | 'stocktaking';
      fromStoreId?: string;
      toStoreId?: string;
      actionNumber?: string;
      description?: string;
      actionDate: Date;
    },
  ) => {
    await createActionUsageRecords(items, {
      ...meta,
      userId: firebaseUser?.uid ?? '',
    });
    load();
  };

  const getEquipmentName = (id: string) =>
    equipment.find(e => e.id === id)?.name ?? id;
  const getEquipmentUnit = (id: string) =>
    equipment.find(e => e.id === id)?.unit ?? '';
  const getStoreName = (id?: string) =>
    id ? (stores.find(s => s.id === id)?.name ?? id) : '—';

  const getUserDisplayName = (userId: string): string => {
    const u = publicUsers[userId];
    if (!u) return t('common.unknownUser');
    const name = `${u.firstname} ${u.lastname}`.trim();
    return name || t('common.unknownUser');
  };

  const formatDate = (session: UsageSession): string => {
    const d = session.actionDate ?? session.createdAt;
    return d.toLocaleDateString();
  };

  const getTypeBadgeStyle = (session: UsageSession) => {
    if (session.type === 'add') return styles.addBadge;
    if (session.type === 'transfer') return styles.transferBadge;
    if (session.type === 'stocktaking') return styles.stocktakingBadge;
    return styles.takeBadge;
  };

  const getTypeBadgeText = (session: UsageSession) => {
    if (session.operationType === 'withdraw') return t('operations.withdraw');
    if (session.type === 'add') return t('usage.typeAdd');
    if (session.type === 'transfer') return t('usage.typeTransfer');
    if (session.type === 'stocktaking') return t('usage.typeStocktaking');
    return t('usage.typeTake');
  };

  const getSessionItemsLabelText = (session: UsageSession) => {
    if (session.operationType === 'withdraw') return t('operations.withdraw');
    if (session.type === 'add') return t('usage.sessionItemsLabelAdd');
    if (session.type === 'transfer') return t('usage.sessionItemsLabelTransfer');
    if (session.type === 'stocktaking') return t('usage.sessionItemsLabelStocktaking');
    return t('usage.sessionItemsLabel');
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const currentStoreName = storeId ? stores.find(s => s.id === storeId)?.name ?? storeId : null;
  const screenTitle = isStoreHistoryMode && currentStoreName
    ? t('usage.storeHistoryTitle').replace('%s', currentStoreName)
    : t('usage.title');

  const displaySessions = searchMode && searchQuery ? searchResults : sessions;
  const displayLoading = searchMode ? searching : loading;

  if (!canView) {
    return (
      <View style={styles.center}>
        <Text style={styles.noAccess}>{t('common.noAccess')}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.innerContent}>
        <View style={styles.header}>
          <Text style={styles.title} numberOfLines={1}>{screenTitle}</Text>
          {isAdminOrManager && !isStoreHistoryMode && !searchMode && (
            <TouchableOpacity
              style={styles.searchBtn}
              onPress={() => setSearchMode(true)}
            >
              <Text style={styles.searchBtnText}>{t('usage.searchByActionNumber')}</Text>
            </TouchableOpacity>
          )}
          {canCreate && !isStoreHistoryMode && !searchMode && (
            <TouchableOpacity
              style={styles.addBtn}
              onPress={() => setWizardVisible(true)}
            >
              <Text style={styles.addBtnText}>{t('common.new')}</Text>
            </TouchableOpacity>
          )}
          {searchMode && (
            <TouchableOpacity
              style={styles.searchCloseBtn}
              onPress={() => {
                setSearchMode(false);
                setSearchInput('');
                setSearchQuery('');
                setSearchResults([]);
              }}
            >
              <Text style={styles.searchCloseBtnText}>✕</Text>
            </TouchableOpacity>
          )}
        </View>

        {searchMode && (
          <View style={styles.searchBar}>
            <TextInput
              style={styles.searchInput}
              value={searchInput}
              onChangeText={setSearchInput}
              placeholder={t('usage.searchPlaceholder')}
              placeholderTextColor="#94A3B8"
              onSubmitEditing={handleSearch}
              returnKeyType="search"
            />
            <TouchableOpacity style={styles.searchActionBtn} onPress={handleSearch}>
              <Text style={styles.searchActionBtnText}>{t('usage.searchButton')}</Text>
            </TouchableOpacity>
          </View>
        )}

        {displayLoading ? (
          <ActivityIndicator style={{ marginTop: 40 }} size="large" color="#2563EB" />
        ) : (
          <FlatList
            data={displaySessions}
            keyExtractor={item => item.id}
            renderItem={({ item }) => {
              const expanded = expandedIds.has(item.id);
              return (
                <TouchableOpacity
                  style={styles.card}
                  onPress={() => toggleExpand(item.id)}
                  activeOpacity={0.85}
                >
                  <View style={styles.cardHeader}>
                    <View style={[styles.typeBadge, getTypeBadgeStyle(item)]}>
                      <Text style={styles.typeBadgeText}>{getTypeBadgeText(item)}</Text>
                    </View>
                    <View style={styles.cardHeaderMeta}>
                      <Text style={styles.itemDate}>{formatDate(item)}</Text>
                      {item.fromStoreId && (
                        <Text style={styles.itemMeta}>
                          {t('usage.fromLabel')}: {getStoreName(item.fromStoreId)}
                        </Text>
                      )}
                      {item.toStoreId && (
                        <Text style={styles.itemMeta}>
                          {t('usage.toLabel')}: {getStoreName(item.toStoreId)}
                        </Text>
                      )}
                      {item.actionNumber ? (
                        <Text style={styles.itemActionNumber}>{item.actionNumber}</Text>
                      ) : null}
                      {item.description ? (
                        <Text style={styles.itemNote}>{item.description}</Text>
                      ) : null}
                      <Text style={styles.itemCreatedBy}>
                        {t('usage.createdBy')}: {getUserDisplayName(item.userId)}
                      </Text>
                      <Text style={styles.itemsCount}>
                        {t('usage.sessionItemsCount').replace('%s', String(item.items.length))}
                      </Text>
                    </View>
                    <Text style={styles.expandChevron}>{expanded ? '▲' : '▼'}</Text>
                  </View>

                  {expanded && (
                    <View style={styles.itemsList}>
                      <Text style={styles.itemsListTitle}>{getSessionItemsLabelText(item)}</Text>
                      {item.items.map(record => (
                        <View key={record.id} style={styles.itemRow}>
                          <Text style={styles.itemRowName}>{getEquipmentName(record.equipmentId)}</Text>
                          <Text style={styles.itemRowQty}>
                            {record.operationType === 'use' ? t('operations.used') : `${record.quantity} ${getEquipmentUnit(record.equipmentId)}`}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}
                </TouchableOpacity>
              );
            }}
            ListHeaderComponent={
              searchMode && searchQuery ? (
                <Text style={styles.searchResultsHeader}>
                  {searchResults.length > 0
                    ? t('usage.searchTitle').replace('%s', searchQuery)
                    : t('usage.searchNoResults').replace('%s', searchQuery)}
                </Text>
              ) : null
            }
            ListEmptyComponent={
              searchMode && !searchQuery ? null : (
                <Text style={styles.empty}>{t('usage.noItems')}</Text>
              )
            }
            ListFooterComponent={loadingMore ? <ActivityIndicator style={styles.footerLoader} color="#2563EB" /> : null}
            onEndReached={loadMore}
            onEndReachedThreshold={0.4}
          />
        )}
      </View>

      <NewUsageWizard
        isAdmin={appUser?.role === 'admin'}
        taskId={params.taskId}
        initialStoreId={params.storeId}
        initialType={params.type === 'stocktaking' || params.taskId ? 'stocktaking' : undefined}
        initialEquipmentId={params.equipmentId}
        onSaved={() => { load().catch(console.error); }}
        visible={wizardVisible}
        onClose={() => {
          setWizardVisible(false);
          launched.current = '';
          router.setParams({ taskId: undefined, create: undefined, type: undefined, equipmentId: undefined });
        }}
        onSave={handleWizardSave}
        categories={categories}
        equipment={equipment}
        stores={stores}
        storeInventory={storeInventory}
        isAdminOrManager={isAdminOrManager}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  innerContent: { flex: 1, maxWidth: 960, width: '100%', alignSelf: 'center' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  noAccess: { fontSize: 18, color: '#6B7280' },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  title: { flex: 1, fontSize: 22, fontWeight: 'bold', color: '#1E293B' },
  addBtn: {
    backgroundColor: '#2563EB',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  addBtnText: { color: '#FFF', fontWeight: '600' },
  searchBtn: {
    backgroundColor: '#64748B',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 8,
  },
  searchBtnText: { color: '#FFF', fontWeight: '600', fontSize: 13 },
  searchCloseBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  searchCloseBtnText: { color: '#64748B', fontWeight: '700', fontSize: 16 },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    backgroundColor: '#F8FAFC',
  },
  searchInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 15,
    color: '#1E293B',
    backgroundColor: '#FFF',
  },
  searchActionBtn: {
    backgroundColor: '#2563EB',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  searchActionBtnText: { color: '#FFF', fontWeight: '600', fontSize: 14 },
  searchResultsHeader: {
    fontSize: 14,
    fontWeight: '600',
    color: '#475569',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 4,
  },
  card: {
    backgroundColor: '#FFF',
    margin: 8,
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  cardHeaderMeta: { flex: 1 },
  typeBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    minWidth: 70,
    alignItems: 'center',
  },
  typeBadgeText: { color: '#FFF', fontWeight: '700', fontSize: 11 },
  takeBadge: { backgroundColor: '#EF4444' },
  addBadge: { backgroundColor: '#22C55E' },
  transferBadge: { backgroundColor: '#6366F1' },
  stocktakingBadge: { backgroundColor: '#F59E0B' },
  itemDate: { fontSize: 12, color: '#CBD5E1', marginBottom: 2 },
  itemMeta: { fontSize: 13, color: '#64748B', marginTop: 2 },
  itemNote: { fontSize: 13, color: '#94A3B8', marginTop: 4, fontStyle: 'italic' },
  itemCreatedBy: { fontSize: 12, color: '#64748B', marginTop: 2 },
  itemActionNumber: { fontSize: 13, color: '#1E293B', fontWeight: '600', marginTop: 4 },
  itemsCount: { fontSize: 12, color: '#94A3B8', marginTop: 4 },
  expandChevron: { fontSize: 12, color: '#94A3B8', marginTop: 2 },
  itemsList: {
    marginTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    paddingTop: 10,
  },
  itemsListTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  itemRowName: { fontSize: 14, color: '#1E293B', flex: 1 },
  itemRowQty: { fontSize: 14, color: '#475569', fontWeight: '600' },
  empty: {
    textAlign: 'center',
    color: '#94A3B8',
    marginTop: 40,
    fontSize: 16,
  },
  footerLoader: { marginVertical: 18 },
});
